import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import { Plus, ChevronRight, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtTime } from "../lib/api";
import { MapPicker, TILE_URL, TILE_ATTR, US_CENTER } from "../components/MapPicker";
import { RatingBlock, ratingTier } from "../components/Badges";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const pinIcon = (r) => L.divIcon({ className: "", html: `<div class="rating-pin ${ratingTier(r)}">${r ?? "–"}</div>`, iconSize: [36, 36], iconAnchor: [18, 36] });

function AddSpotDialog({ open, onOpenChange, onAdded }) {
  const [name, setName] = useState("");
  const [pin, setPin] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/spots", { name, ...pin });
      onAdded(data);
      setName(""); setPin(null);
      onOpenChange(false);
      toast.success("Spot saved");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-2 border-[#FF4D00] max-w-lg max-h-[92vh] overflow-y-auto" data-testid="add-spot-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase text-2xl">New spot</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-5">
          <div>
            <label className="brut-label">Spot name</label>
            <input className="brut-input" required maxLength={60} placeholder="North jetty" value={name} onChange={(e) => setName(e.target.value)} data-testid="add-spot-name-input" />
          </div>
          <div>
            <label className="brut-label">Location</label>
            <MapPicker value={pin} onChange={setPin} height={240} testId="add-spot-map" />
          </div>
          <button className="brut-btn w-full" disabled={busy || !pin || !name.trim()} data-testid="add-spot-save-btn">{busy ? "Saving…" : "Save spot"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Spots() {
  const nav = useNavigate();
  const [spots, setSpots] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/spots").then((r) => setSpots(r.data)).catch(() => setSpots([])); }, []);

  const bounds = useMemo(() => (spots?.length ? L.latLngBounds(spots.map((s) => [s.lat, s.lng])).pad(0.3) : null), [spots]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <h1 className="font-display text-4xl sm:text-5xl uppercase leading-none">Spots<br /><span className="text-[#FF4D00]">&amp; bite</span></h1>
        <button className="brut-btn !min-h-[44px] !px-4" onClick={() => setOpen(true)} data-testid="add-spot-btn"><Plus size={18} /> Spot</button>
      </div>

      <div className="border-2 border-[#FF4D00] h-72 sm:h-96 shadow-[4px_4px_0_0_#FF4D00]" data-testid="spots-map">
        {spots !== null && (
          <MapContainer key={spots.length} bounds={bounds || undefined} center={bounds ? undefined : US_CENTER} zoom={bounds ? undefined : 4}
            maxZoom={15} className="h-full w-full">
            <TileLayer url={TILE_URL} attribution={TILE_ATTR} />
            {spots.map((s) => (
              <Marker key={s.id} position={[s.lat, s.lng]} icon={pinIcon(s.best_today?.rating)} eventHandlers={{ click: () => nav(`/spots/${s.id}`) }} />
            ))}
          </MapContainer>
        )}
      </div>
      <p className="text-xs text-zinc-500">Numbers are today's best bite rating (1–10). Tap a pin or a spot for details.</p>

      {spots === null && <p className="text-zinc-500" data-testid="spots-loading">Checking conditions…</p>}
      {spots?.length === 0 && (
        <div className="brut-card p-8 text-center space-y-3" data-testid="spots-empty">
          <MapPin size={36} className="mx-auto text-[#FF4D00]" />
          <p className="text-zinc-400">Save your go-to spots to see daily bite ratings.</p>
        </div>
      )}
      <div className="space-y-3">
        {spots?.map((s) => (
          <Link key={s.id} to={`/spots/${s.id}`} data-testid={`spot-row-${s.id}`}
            className="brut-card p-4 flex items-center gap-4 hover:border-[#FF4D00] transition-[border-color]">
            <RatingBlock rating={s.best_today?.rating} testId={`spot-row-rating-${s.id}`} />
            <div className="flex-1 min-w-0">
              <h3 className="font-display uppercase truncate">{s.name}</h3>
              <p className="text-xs text-zinc-400">
                {s.best_today ? `Best today ${fmtTime(s.best_today.time)}` : "Conditions unavailable"}
                {s.tide_available === false && " · no tide data"}
              </p>
            </div>
            <ChevronRight className="text-zinc-500" />
          </Link>
        ))}
      </div>
      <AddSpotDialog open={open} onOpenChange={setOpen} onAdded={(s) => setSpots((prev) => [s, ...(prev || [])])} />
    </div>
  );
}
